import React from 'react';
import { Plus, Trash2, Ticket, Tag, Wallet } from 'lucide-react';

const inputCls = 'w-full px-3 py-2 bg-white border border-neutral-200 rounded-sm font-sans-ah text-[13px] focus:outline-none focus:border-[#4a9cd4]';
const lbl = 'font-sans-ah text-[11px] tracking-wide-ah text-[#777] uppercase block mb-1';
const addBtn = 'font-sans-ah text-[12px] tracking-wide-ah px-3 py-2 rounded-sm border border-dashed border-[#4a9cd4] text-[#4a9cd4] hover:bg-white flex items-center gap-1.5';
const uid = () => Math.random().toString(36).slice(2, 10);

export const DEFAULT_PAYMENT = { pay_at_door: true, etransfer: false, etransfer_email: '', online: false, online_link: '', instructions: '' };
export const PRESET_MEMBER_PRICING = [
  { id: 'member', name: 'Member', price: 0, members_only: true, description: '' },
  { id: 'nonmember', name: 'Non-member', price: 10, members_only: false, description: '' },
];

function Section({ icon: Icon, title, children, testid }) {
  return (
    <div className="mt-6 pt-5 border-t border-neutral-200" data-testid={testid}>
      <h4 className="font-serif-ah text-[17px] text-[#2f2f2f] mb-3 flex items-center gap-2"><Icon size={16} className="text-[#4a9cd4]" /> {title}</h4>
      {children}
    </div>
  );
}

function TicketTypes({ types, onChange }) {
  const upd = (i, k, v) => onChange(types.map((t, j) => (j === i ? { ...t, [k]: v } : t)));
  return (
    <Section icon={Ticket} title="Tickets & Pricing" testid="event-ticket-types">
      {types.length === 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <p className="font-sans-ah text-[13px] text-[#888] mr-2">Free event, guests just choose how many are coming.</p>
          <button type="button" onClick={() => onChange(PRESET_MEMBER_PRICING.map((t) => ({ ...t, id: uid() })))} data-testid="ticket-preset-member-btn" className={addBtn}>
            Use "Free for members / $10 non-members"
          </button>
        </div>
      )}
      <div className="space-y-2">
        {types.map((t, i) => (
          <div key={t.id} className="grid grid-cols-12 gap-2 items-end bg-white border border-neutral-200 rounded-sm p-3" data-testid={`ticket-type-row-${i}`}>
            <div className="col-span-12 sm:col-span-4"><label className={lbl}>Ticket name</label>
              <input className={inputCls} value={t.name} onChange={(e) => upd(i, 'name', e.target.value)} placeholder="e.g. Non-member" data-testid={`ticket-name-${i}`} /></div>
            <div className="col-span-5 sm:col-span-2"><label className={lbl}>Price ($)</label>
              <input type="number" min="0" step="0.01" className={inputCls} value={t.price} onChange={(e) => upd(i, 'price', e.target.value === '' ? '' : Number(e.target.value))} data-testid={`ticket-price-${i}`} /></div>
            <div className="col-span-7 sm:col-span-4"><label className={lbl}>Note (optional)</label>
              <input className={inputCls} value={t.description} onChange={(e) => upd(i, 'description', e.target.value)} placeholder="e.g. Includes dinner" data-testid={`ticket-desc-${i}`} /></div>
            <label className="col-span-8 sm:col-span-1 flex items-center gap-1.5 font-sans-ah text-[12px] text-[#555] pb-2" title="Guests must enter their member business name">
              <input type="checkbox" checked={t.members_only} onChange={(e) => upd(i, 'members_only', e.target.checked)} data-testid={`ticket-members-only-${i}`} /> Members
            </label>
            <button type="button" onClick={() => onChange(types.filter((_, j) => j !== i))} aria-label="Remove ticket type" data-testid={`ticket-remove-${i}`} className="col-span-4 sm:col-span-1 justify-self-end p-2 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...types, { id: uid(), name: '', price: 0, members_only: false, description: '' }])} data-testid="ticket-add-btn" className={`${addBtn} mt-3`}>
        <Plus size={14} /> ADD TICKET TYPE
      </button>
      {types.length > 0 && <p className="font-sans-ah text-[12px] text-[#999] mt-2">Set a price of 0 for free tickets (e.g. "Sponsor – Free"). Tick "Members" to ask for the member business name.</p>}
    </Section>
  );
}

function PromoCodes({ codes, onChange }) {
  const upd = (i, k, v) => onChange(codes.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  return (
    <Section icon={Tag} title="Promo & Comp Codes" testid="event-promo-codes">
      <div className="space-y-2">
        {codes.map((c, i) => (
          <div key={c.id} className="grid grid-cols-12 gap-2 items-end bg-white border border-neutral-200 rounded-sm p-3" data-testid={`promo-row-${i}`}>
            <div className="col-span-6 sm:col-span-3"><label className={lbl}>Code</label>
              <input className={`${inputCls} uppercase`} value={c.code} onChange={(e) => upd(i, 'code', e.target.value.toUpperCase().replace(/\s/g, ''))} placeholder="EARLYBIRD" data-testid={`promo-code-${i}`} /></div>
            <div className="col-span-6 sm:col-span-2"><label className={lbl}>Type</label>
              <select className={inputCls} value={c.kind} onChange={(e) => upd(i, 'kind', e.target.value)} data-testid={`promo-kind-${i}`}>
                <option value="percent">% off</option><option value="amount">$ off</option>
              </select></div>
            <div className="col-span-4 sm:col-span-2"><label className={lbl}>{c.kind === 'percent' ? 'Percent' : 'Amount ($)'}</label>
              <input type="number" min="0" step="0.01" className={inputCls} value={c.value} onChange={(e) => upd(i, 'value', e.target.value === '' ? '' : Number(e.target.value))} data-testid={`promo-value-${i}`} /></div>
            <div className="col-span-4 sm:col-span-2"><label className={lbl}>Max uses</label>
              <input type="number" min="1" className={inputCls} value={c.max_uses ?? ''} placeholder="No limit" onChange={(e) => upd(i, 'max_uses', e.target.value === '' ? null : Number(e.target.value))} data-testid={`promo-max-${i}`} /></div>
            <div className="col-span-4 sm:col-span-2"><label className={lbl}>Expires</label>
              <input type="date" className={inputCls} value={c.expires} onChange={(e) => upd(i, 'expires', e.target.value)} data-testid={`promo-expires-${i}`} /></div>
            <button type="button" onClick={() => onChange(codes.filter((_, j) => j !== i))} aria-label="Remove code" data-testid={`promo-remove-${i}`} className="col-span-12 sm:col-span-1 justify-self-end p-2 text-red-500 hover:bg-red-50 rounded-sm"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button type="button" onClick={() => onChange([...codes, { id: uid(), code: '', kind: 'percent', value: 20, max_uses: null, expires: '' }])} data-testid="promo-add-btn" className={addBtn}><Plus size={14} /> ADD DISCOUNT CODE</button>
        <button type="button" onClick={() => onChange([...codes, { id: uid(), code: 'COMP', kind: 'percent', value: 100, max_uses: 10, expires: '' }])} data-testid="promo-add-comp-btn" className={addBtn}><Plus size={14} /> ADD FREE (COMP) CODE</button>
      </div>
    </Section>
  );
}

function Payment({ pay, onChange }) {
  const set = (k, v) => onChange({ ...pay, [k]: v });
  const check = (k, label) => (
    <label className="flex items-center gap-2 font-sans-ah text-[13px] text-[#444]">
      <input type="checkbox" checked={!!pay[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`pay-option-${k}`} /> {label}
    </label>
  );
  return (
    <Section icon={Wallet} title="Payment Options (for paid tickets)" testid="event-payment-options">
      <div className="space-y-3">
        {check('pay_at_door', 'Pay at the door')}
        {check('etransfer', 'E-transfer')}
        {pay.etransfer && <input className={inputCls} value={pay.etransfer_email} onChange={(e) => set('etransfer_email', e.target.value)} placeholder="E-transfer email (defaults to the Chamber inbox)" data-testid="pay-etransfer-email" />}
        {check('online', 'Pay online with a payment link')}
        {pay.online && <input className={inputCls} value={pay.online_link} onChange={(e) => set('online_link', e.target.value)} placeholder="https://… (Square, PayPal, Stripe or Zeffy payment link)" data-testid="pay-online-link" />}
        <div><label className={lbl}>Payment note shown to guests (optional)</label>
          <input className={inputCls} value={pay.instructions} onChange={(e) => set('instructions', e.target.value)} placeholder="e.g. Cash or card accepted at the door" data-testid="pay-instructions" /></div>
      </div>
    </Section>
  );
}

export function EventPricing({ value, onChange }) {
  const types = value.ticket_types || [];
  return (
    <>
      <TicketTypes types={types} onChange={(ticket_types) => onChange({ ...value, ticket_types })} />
      {types.length > 0 && (
        <>
          <PromoCodes codes={value.promo_codes || []} onChange={(promo_codes) => onChange({ ...value, promo_codes })} />
          <Payment pay={{ ...DEFAULT_PAYMENT, ...(value.payment || {}) }} onChange={(payment) => onChange({ ...value, payment })} />
        </>
      )}
    </>
  );
}
